import type { PromotionSummary as Promotion } from '@/types/promotions';
import { Fragment, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, Transition } from '@headlessui/react';
import { X, Check } from 'lucide-react';
import { ProductHuntMark, TrialTimeline } from '@/components/marketing/OfferParts';
import type { Plan } from '@/types';
import { subscriptionApi } from '@/services/api';
import { AlertDialog } from '../ui/AlertDialog';
import { toast } from 'sonner';

import { useTranslation } from 'react-i18next';
// Custom Upgrade Icon with gradient
const UpgradeIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="upgradeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#06b6d4" />
        <stop offset="50%" stopColor="#a855f7" />
        <stop offset="100%" stopColor="#ec4899" />
      </linearGradient>
    </defs>
    {/* Circle */}
    <path
      d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z"
      stroke="url(#upgradeGradient)"
      strokeWidth="2"
      fill="none"
    />
    {/* Arrow Up */}
    <path
      d="M12 7v10M8 11l4-4 4 4"
      stroke="url(#upgradeGradient)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Matches the backend's TrialService::LENGTH_IN_DAYS; offers must be paid within it. */
const TRIAL_DAYS = 14;

interface UpgradeModalProps {
  promotion?: Promotion;
  campaignId?: string;
  isOpen: boolean;
  onClose: () => void;
  currentPlan: string | null;
  requiredFeature?: string;
  plans: Plan[];
  isTrial?: boolean;
  /** Whole trial days left, for the carry-over note under each plan. */
  trialDaysLeft?: number;
  currentBoardCount?: number;
}

/** The subscription the open checkout replaces; set when checkout starts, read when Paddle reports payment. */
let subscriptionBeforeCheckout: string | null = null;

/**
 * Reload once the payment webhook has made the new subscription current. Paddle
 * reports the payment before our webhook is processed, and reloading straight
 * away showed the trial (and its offer) the customer had just paid to replace.
 */
async function reloadWhenPlanChanges(timeoutMs = 30_000) {
  const until = Date.now() + timeoutMs;
  while (Date.now() < until) {
    await new Promise(resolve => setTimeout(resolve, 1500));
    try {
      const current = (await subscriptionApi.getCurrent()).data?.subscription;
      if (current?.paddle_subscription_id && current.id !== subscriptionBeforeCheckout) break;
    } catch { /* Keep waiting; the reload below still happens. */ }
  }
  window.location.reload();
}

const formatPlatformName = (platform: string) => {
  if (platform.toLowerCase() === 'tiktok') return 'TikTok';
  if (platform.toLowerCase() === 'youtube') return 'YouTube';
  return platform.charAt(0).toUpperCase() + platform.slice(1);
};

const formatPlatformList = (platforms: string[]) => {
  if (!platforms.length) return 'No connected platforms';
  if (platforms.length === 1) return formatPlatformName(platforms[0] ?? '');
  const names = platforms.map(formatPlatformName);
  const lastName = names[names.length - 1] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${lastName}`;
};

const formatBoardLimit = (maxBoards: number) => {
  if (maxBoards === -1) return 'Unlimited board workspaces';
  return `${maxBoards} board workspace${maxBoards === 1 ? '' : 's'}`;
};

const getPlanMonthlyAiCredits = (plan: Plan) => {
  if (typeof plan.monthly_ai_credits === 'number') {
    return plan.monthly_ai_credits;
  }

  const fallbackCredits: Record<string, number> = {
    starter: 100,
    pro: 500,
    agency: 2000,
  };

  return fallbackCredits[plan.tier] ?? 0;
};

const formatCredits = (credits: number) => credits.toLocaleString();

const formatRequiredFeatureMessage = (message: string) => {
  const trimmed = message.trim();
  if (!trimmed) return '';
  if (/[.!?]$/.test(trimmed)) return trimmed;
  return `${trimmed} requires a higher plan.`;
};

export default function UpgradeModal({
  isOpen,
  onClose,
  currentPlan,
  requiredFeature,
  plans,
  isTrial = false,
  trialDaysLeft,
  currentBoardCount = 0,
  promotion,
  campaignId,
}: UpgradeModalProps) {
  const { t, i18n } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annually'>('monthly');

  const [offers,setOffers]=useState<Promotion[]>([]);
  const submitting=useRef(false);
  useEffect(()=>{
    if(!isOpen)return;
    let live=true;
    const show=(list:Promotion[])=>{setOffers(list);if(list[0])setBillingInterval(list[0].intervals[0]||'monthly');};
    if(promotion){show([promotion]);}
    else{setOffers([]);subscriptionApi.getPromotions().then(r=>{if(live)show(r.data||[]);}).catch(()=>{/* The existing regular checkout remains available. */});}
    return()=>{live=false;};
  },[isOpen,promotion]);
  const applicable=(plan:Plan)=>offers.find(p=>p.plan_ids.includes(plan.id)&&p.intervals.includes(billingInterval));

  // Initialize Paddle.js when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (window.Paddle) return; // Already loaded

    const script = document.createElement('script');
    script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
    script.async = true;
    script.onload = () => {
      if (window.Paddle) {
        const paddleEnv = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox';
        const paddleToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
        window.Paddle.Environment.set(paddleEnv);
        window.Paddle.Initialize({
          token: paddleToken,
          eventCallback: (data: any) => {
            console.log('Paddle event:', data);
            // Reload the page when checkout completes to refresh subscription state
            if (data?.name === 'checkout.completed') {
              toast.success(t('upgradeModal.paymentCompleted'));
              void reloadWhenPlanChanges();
            }
          },
        });
      }
    };
    document.head.appendChild(script);
  }, [isOpen]);

  const money = (amount: number) => `$${+amount.toFixed(2)}`;

  /** Price per month, less `off` percent for an introductory offer. */
  const formatPrice = (plan: Plan, off = 0) => {
    const monthlyPrice = parseFloat(plan.price);
    if (monthlyPrice === 0) return 'Free';
    const keep = 1 - off / 100;
    if (billingInterval === 'annually' && plan.annual_price) {
      const annualMonthly = parseFloat(plan.annual_price) * keep / 12;
      return `$${annualMonthly.toFixed(2)}/mo`;
    }
    return `${money(monthlyPrice * keep)}/mo`;
  };

  /** What the first payment costs and what renewals cost afterwards. */
  const offerLine = (plan: Plan, offer: Promotion) => {
    if (billingInterval === 'annually' && plan.annual_price) {
      const annual = parseFloat(plan.annual_price);
      return t('promotions.offerLine.annually', { price: money(annual * (1 - offer.percentage / 100)), regular: money(annual) });
    }
    return t('promotions.offerLine.monthly', { regular: money(parseFloat(plan.price)) });
  };

  const formatDaysLimit = (days?: number | null) => {
    if (!days || days < 0) return 'Unlimited';
    return `${days} day${days === 1 ? '' : 's'}`;
  };

  const isPlanHigherTier = (planTier: string, currentTier: string) => {
    const tierOrder = ['none', 'free', 'starter', 'pro', 'agency'];
    return tierOrder.indexOf(planTier) > tierOrder.indexOf(currentTier);
  };

  const handleUpgradeClick = (plan: Plan) => {
    // Paddle's checkout already shows the discounted total before anything is
    // charged, so an offer goes straight there instead of through a second dialog.
    if (promotion || applicable(plan)) void startCheckout(plan);
    else setSelectedPlan(plan);
  };

  const startCheckout = async (selectedPlan: Plan) => {
    if (submitting.current) return;
    const selectedOffer=applicable(selectedPlan);
    if (promotion && !selectedOffer) {toast.error(t('promotions.changed'));return;}
    submitting.current=true;

    setIsUpgrading(true);
    try {
      const [response, current] = await Promise.all([
        subscriptionApi.upgradePlan(selectedPlan.id, billingInterval, selectedOffer?.id, campaignId),
        subscriptionApi.getCurrent().catch(() => null),
      ]);
      subscriptionBeforeCheckout = current?.data?.subscription?.id ?? null;
      onClose();

      const paddleTxnId = response.data?.paddle_transaction_id;

      // Use Paddle.js inline checkout overlay (no redirect needed)
      if (window.Paddle && paddleTxnId) {
        window.Paddle.Checkout.open({
          transactionId: paddleTxnId,
        });
      } else if (response.data?.approval_url) {
        // Fallback: open the hosted checkout URL in a new tab
        window.open(response.data.approval_url, '_blank');
      } else {
        toast.error(t('upgradeModal.checkoutOpenError'));
        window.location.reload();
      }
    } catch (error: any) {
      console.error('Upgrade failed:', error);
      const code=error.response?.data?.error_code;
      const known: Record<string, string> = {
        checkout_in_progress: 'promotions.checkoutPending',
        promotion_checkout_unconfirmed: 'promotions.checkoutPending',
        checkout_already_paid: 'promotions.alreadyPaid',
        checkout_retry: 'promotions.checkoutRetry',
        offer_window_closed: 'promotions.reason.offer_window_closed',
        promotion_unavailable: 'promotions.changed',
        plan_not_supported: 'promotions.changed',
        quota_reached: 'promotions.reason.quota_reached',
      };
      toast.error(code && known[code] ? t(known[code]) : (error.response?.data?.message || t('promotions.checkoutError')));
    } finally {
      submitting.current=false;
      setIsUpgrading(false);
      setSelectedPlan(null);
    }
  };

  if (typeof document === 'undefined') return null;

  const notice = offers.find(p => p.intervals.includes(billingInterval)) ?? offers[0];
  const noticeDaysLeft = notice ? Math.min(TRIAL_DAYS, Math.max(1, Math.ceil((new Date(notice.expires_at).getTime() - Date.now()) / 86_400_000))) : 0;
  const noticeDate = notice && new Date(notice.expires_at).toLocaleString(i18n.language.startsWith('id') ? 'id-ID' : 'en-GB', { timeZone: 'Asia/Jakarta', dateStyle: 'medium', timeStyle: 'short' });

  return createPortal(
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[10000]" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 md:p-8 shadow-2xl transition-all border border-gray-200 dark:border-gray-800">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <Dialog.Title className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <UpgradeIcon className="w-6 h-6 md:w-7 md:h-7" />{t('subscription.upgrade.heading')}</Dialog.Title>
                      {requiredFeature && (
                        <p className="mt-2 text-sm md:text-base text-gray-600 dark:text-gray-400">
                          {formatRequiredFeatureMessage(requiredFeature)} Choose the perfect plan for your needs.
                        </p>
                      )}
                    </div>
                    <button
                      onClick={onClose}
                      className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>

                  {/* Billing Toggle */}
                  <div className="flex flex-col items-center justify-center mb-6">
                    <div className="relative flex justify-center items-center gap-3">
                      <span className={`text-sm font-semibold transition-colors ${
                        billingInterval === 'monthly' ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
                      }`}>{t('subscription.upgrade.monthly')}</span>
                      <button
                        aria-label={t('promotions.toggleBilling')}
                        // An offer for one interval only: switching would show plans the offer cannot buy.
                        disabled={Boolean(promotion && promotion.intervals.length < 2)}
                        onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'annually' : 'monthly')}
                        className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                          billingInterval === 'annually'
                            ? 'bg-gradient-to-r from-primary-500 to-purple-500'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                          billingInterval === 'annually' ? 'translate-x-8' : 'translate-x-1'
                        }`} />
                      </button>
                      <span className={`text-sm font-semibold transition-colors ${
                        billingInterval === 'annually' ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'
                      }`}>{t('subscription.upgrade.annual')}</span>
                      <div className="absolute left-full ml-3 flex items-center whitespace-nowrap">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold border transition-colors ${
                            billingInterval === 'annually'
                              ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 animate-pulse'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          2 months free
                        </span>
                      </div>
                    </div>
                  </div>

                  {notice ? (
                    <div className="mx-auto mb-6 max-w-2xl rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-800/40">
                      <p className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
                        <ProductHuntMark className="h-2.5 w-2.5 shrink-0" />
                        {t('promotions.modalNotice', { percentage: notice.percentage, date: noticeDate })}
                      </p>
                      <TrialTimeline total={TRIAL_DAYS} left={noticeDaysLeft} paid className="mt-3 h-2" />
                      <div className="mt-1.5 flex justify-between gap-4 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                        <span>{t('promoBanner.timelineLeft', { count: noticeDaysLeft })}</span>
                        <span>{t('promoBanner.timelinePaid', { percentage: notice.percentage, period: t(billingInterval === 'annually' ? 'promoBanner.periodYear' : 'promoBanner.periodMonth') })}</span>
                      </div>
                    </div>
                  ) : null}

                  {/* Plans Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {plans.map((plan) => {
                      const isCurrentPlan = plan.tier === currentPlan && !isTrial;
                      const isTrialPlan = plan.tier === currentPlan && isTrial;
                      const boardRemovalCount = plan.max_boards === -1
                        ? 0
                        : Math.max(0, currentBoardCount - plan.max_boards);
                      const isBlockedByBoardLimit = boardRemovalCount > 0;
                      const canUpgrade = !isBlockedByBoardLimit && (
                        !currentPlan ||
                        isPlanHigherTier(plan.tier, currentPlan) ||
                        (isTrial && plan.tier !== currentPlan)
                      );

                      const offer = applicable(plan);

                      return (
                        <div
                          key={plan.id}
                          className={`relative rounded-xl border-2 p-5 md:p-6 transition-all ${isCurrentPlan || isTrialPlan
                            ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10'
                            : canUpgrade
                              ? 'border-purple-200 dark:border-purple-700 bg-white dark:bg-gray-800 hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-lg'
                              : isBlockedByBoardLimit
                                ? 'border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/10'
                                : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 opacity-60'
                            }`}
                        >
                          {(isCurrentPlan || isTrialPlan) && (
                            <div className="absolute top-4 right-4">
                              <span className="bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 text-xs font-semibold px-2 py-1 rounded-full">
                                {isTrialPlan ? 'Trial Active' : 'Current'}
                              </span>
                            </div>
                          )}

                          {/* Plan Header */}
                          <div className="mb-4">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{plan.name}</h3>
                            <div className="mb-1 flex flex-wrap items-baseline gap-x-2">
                              {offer && (
                                <span aria-hidden="true" className="text-lg font-semibold text-gray-400 line-through dark:text-gray-500">{formatPrice(plan)}</span>
                              )}
                              <span className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">{formatPrice(plan, offer?.percentage)}</span>
                            </div>
                            {offer && (
                              <p className="mb-2 text-xs font-medium text-primary-700 dark:text-primary-300">{offerLine(plan, offer)}</p>
                            )}
                            {!offer && billingInterval === 'annually' && plan.annual_price && parseFloat(plan.price) > 0 && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                Billed ${parseFloat(plan.annual_price).toFixed(0)}/year
                              </p>
                            )}
                            <p className="text-sm text-gray-600 dark:text-gray-400">{plan.description}</p>
                          </div>

                          {/* Features List */}
                          <div className="space-y-2.5 mb-6">
                            <div className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {formatBoardLimit(plan.max_boards)}
                              </span>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {formatPlatformList(plan.allowed_platforms)} included
                              </span>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-300">{t('subscription.upgrade.oneAccountPerPlatform')}</span>
                            </div>
                            {plan.has_oauth_analytics && (
                              <div className="flex items-start gap-2 text-sm">
                                <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700 dark:text-gray-300">{t('subscription.upgrade.advancedAnalytics')}</span>
                              </div>
                            )}
                            {plan.has_content_analytics && (
                              <div className="flex items-start gap-2 text-sm">
                                <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700 dark:text-gray-300">{t('subscription.upgrade.contentAnalytics')}</span>
                              </div>
                            )}
                            <div className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {formatCredits(getPlanMonthlyAiCredits(plan))} AI credits/month
                              </span>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {formatDaysLimit(plan.analytics_history_days)} analytics history
                              </span>
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-700 dark:text-gray-300">
                                {formatDaysLimit(plan.sync_period_days)} analytics sync period
                              </span>
                            </div>
                            {plan.has_google_calendar && (
                              <div className="flex items-start gap-2 text-sm">
                                <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700 dark:text-gray-300">{t('subscription.upgrade.googleCalendar')}</span>
                              </div>
                            )}
                            {plan.has_team_collaboration && (
                              <div className="flex items-start gap-2 text-sm">
                                <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700 dark:text-gray-300">{t('subscription.upgrade.teamCollaboration')}</span>
                              </div>
                            )}
                            {plan.has_white_label && (
                              <div className="flex items-start gap-2 text-sm">
                                <Check className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                                <span className="text-gray-700 dark:text-gray-300">{t('subscription.upgrade.whiteLabel')}</span>
                              </div>
                            )}
                          </div>

                          {/* Action Button */}
                          {canUpgrade && (
                            <button
                              onClick={() => handleUpgradeClick(plan)}
                              disabled={isUpgrading}
                              className="w-full disabled:cursor-wait disabled:opacity-60 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 dark:from-purple-500 dark:to-purple-600 dark:hover:from-purple-600 dark:hover:to-purple-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-all shadow-md hover:shadow-lg"
                            >
                              {offer || promotion ? t('promotions.selectPlan',{plan:plan.name}) : (isTrial ? t('upgradeModal.subscribe') : t('upgradeModal.upgradeNow'))}
                            </button>
                          )}
                          {isBlockedByBoardLimit && (
                            <div className="space-y-2">
                              <button
                                disabled
                                className="w-full bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 font-semibold py-2.5 px-4 rounded-lg cursor-not-allowed"
                              >
                                Remove {boardRemovalCount} board{boardRemovalCount === 1 ? '' : 's'} to choose {plan.name}
                              </button>
                              <p className="text-xs leading-5 text-amber-700/80 dark:text-amber-300/80">{t('subscription.upgrade.boardsSafeNote')}</p>
                            </div>
                          )}
                          {!isBlockedByBoardLimit && isTrialPlan && (
                            <button
                              onClick={() => handleUpgradeClick(plan)}
                              disabled={isUpgrading}
                              className="w-full disabled:cursor-wait disabled:opacity-60 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-all shadow-md hover:shadow-lg"
                            >{t('subscription.upgrade.subscribeNow')}</button>
                          )}
                          {!isBlockedByBoardLimit && isCurrentPlan && (
                            <button
                              disabled
                              className="w-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 font-semibold py-2.5 px-4 rounded-lg cursor-not-allowed"
                            >{t('subscription.upgrade.currentPlan')}</button>
                          )}
                          {!canUpgrade && !isCurrentPlan && !isTrialPlan && !isBlockedByBoardLimit && (
                            <button
                              disabled
                              className="w-full bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 font-semibold py-2.5 px-4 rounded-lg cursor-not-allowed"
                            >{t('subscription.upgrade.lowerTier')}</button>
                          )}
                          {/* Said where the decision is made: paying now does not cost the rest of the trial. */}
                          {isTrial && !isBlockedByBoardLimit && (canUpgrade || isTrialPlan) && (
                            <p className="mt-2.5 text-center text-xs leading-5 text-gray-500 dark:text-gray-400">
                              {trialDaysLeft
                                ? t('upgradeModal.carryOver', { count: trialDaysLeft, period: t(billingInterval === 'annually' ? 'promoBanner.periodYear' : 'promoBanner.periodMonth') })
                                : t('upgradeModal.carryOverGeneric', { period: t(billingInterval === 'annually' ? 'promoBanner.periodYear' : 'promoBanner.periodMonth') })}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer */}
                  <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">{t('subscription.upgrade.needHelp')}</div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <AlertDialog
        open={!!selectedPlan}
        onClose={() => setSelectedPlan(null)}
        onConfirm={() => { if (selectedPlan) void startCheckout(selectedPlan); }}
        title={t('upgradeModal.confirmTitle', { plan: selectedPlan?.name })}
        description={t(isTrial ? 'upgradeModal.confirmDescriptionTrial' : 'upgradeModal.confirmDescription', { plan: selectedPlan?.name })}
        confirmText={t('upgradeModal.confirm')}
        cancelText={t('upgradeModal.cancel')}
        variant="info"
        isLoading={isUpgrading}
      />
    </>,
    document.body
  );
}
